import { Checkbox, ColumnDefinition, Table, TextField } from '@approvalmax/ui/src/components';
import { netSuiteConstants } from 'modules/common/constants';
import {
    isFieldReadOnly,
    isFieldVisible,
} from 'pages/requestForm/containers/netsuite/NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { Control } from 'react-hook-form';
import { useNetSuiteVendorFieldSettings } from 'shared/hooks';

import { AddressLinesTableItem, FormValues } from '../../NetSuiteVendorCreationPopupContent.types';
import { CountryCell } from '../CountryCell/CountryCell';
import { messages } from './AddressLinesInformation.messages';

export const getColumnDefinition = (
    control: Control<FormValues>,
    fieldSettings: ReturnType<typeof useNetSuiteVendorFieldSettings>
) => {
    const columnDefinition: ColumnDefinition<AddressLinesTableItem>[] = [
        {
            id: 'defaultBilling',
            name: messages.defaultBilling,
            minWidth: 100,
            textAlign: 'center',

            cellComponent: (props) => (
                <Table.FormCell {...props} name={`addressLines.${props.item.index}.defaultBilling`} columnSpacing='4 8'>
                    {(fieldProps) => <Checkbox {...fieldProps} size='small' />}
                </Table.FormCell>
            ),
        },
        {
            id: 'defaultShipping',
            name: messages.defaultShipping,
            minWidth: 110,
            textAlign: 'center',
            cellComponent: (props) => (
                <Table.FormCell
                    {...props}
                    name={`addressLines.${props.item.index}.defaultShipping`}
                    columnSpacing='4 8'
                >
                    {(fieldProps) => <Checkbox {...fieldProps} size='small' />}
                </Table.FormCell>
            ),
        },
        {
            id: 'label',
            name: messages.label,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} name={`addressLines.${props.item.index}.label`} columnSpacing='4 8'>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.labelMaxLength}
                            bordered={false}
                            size='small'
                        />
                    )}
                </Table.FormCell>
            ),
        },
    ];

    if (isFieldVisible(fieldSettings.addressLinesCountry)) {
        columnDefinition.push({
            id: 'country',
            name: messages.country,
            minWidth: 180,
            cellComponent: (props) => (
                <CountryCell
                    {...props}
                    name={`addressLines.${props.item.index}.countryId`}
                    disabled={isFieldReadOnly(fieldSettings.addressLinesCountry)}
                    columnSpacing='4 8'
                />
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesAttention)) {
        columnDefinition.push({
            id: 'attention',
            name: messages.attention,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} name={`addressLines.${props.item.index}.attention`} columnSpacing='4 8'>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.attentionMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesAttention)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesAddressee)) {
        columnDefinition.push({
            id: 'addressee',
            name: messages.addressee,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} columnSpacing='4 8' name={`addressLines.${props.item.index}.addressee`}>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.addresseeMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesAddressee)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesPhone)) {
        columnDefinition.push({
            id: 'phone',
            name: messages.phone,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} columnSpacing='4 8' name={`addressLines.${props.item.index}.phone`}>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.phoneMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesPhone)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesPrimaryAddress)) {
        columnDefinition.push({
            id: 'primaryAddress',
            name: messages.primaryAddress,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} name={`addressLines.${props.item.index}.primaryAddress`} columnSpacing='4 8'>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.primaryAddressMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesPrimaryAddress)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesSecondaryAddress)) {
        columnDefinition.push({
            id: 'secondaryAddress',
            name: messages.secondaryAddress,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell
                    {...props}
                    columnSpacing='4 8'
                    name={`addressLines.${props.item.index}.secondaryAddress`}
                >
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.secondaryAddressMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesSecondaryAddress)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesState)) {
        columnDefinition.push({
            id: 'state',
            name: messages.state,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} columnSpacing='4 8' name={`addressLines.${props.item.index}.state`}>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.stateMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesState)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesCity)) {
        columnDefinition.push({
            id: 'city',
            name: messages.city,
            minWidth: 180,
            cellComponent: (props) => (
                <Table.FormCell {...props} columnSpacing='4 8' name={`addressLines.${props.item.index}.city`}>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.cityMaxLength}
                            bordered={false}
                            size='small'
                            readOnly={isFieldReadOnly(fieldSettings.addressLinesCity)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    if (isFieldVisible(fieldSettings.addressLinesZip)) {
        columnDefinition.push({
            id: 'zip',
            name: messages.zip,
            minWidth: 120,
            cellComponent: (props) => (
                <Table.FormCell {...props} columnSpacing='4 8' name={`addressLines.${props.item.index}.zip`}>
                    {(fieldProps) => (
                        <TextField
                            {...fieldProps}
                            placeholder={messages.inputPlaceholder}
                            maxLength={netSuiteConstants.zipMaxLength}
                            bordered={false}
                            size='small'
                            disabled={isFieldReadOnly(fieldSettings.addressLinesZip)}
                        />
                    )}
                </Table.FormCell>
            ),
        });
    }

    return columnDefinition;
};
